import { BaseSyntheticEvent, useCallback, useState } from 'react';
// Redux
import { useSelector, useDispatch } from 'react-redux';
import {
  setShowCreateFreebetsForm,
  setShowCancelFreebetsForm,
  setShowRevokeFreebetsForm,
} from '@Modal/reducers/freebets/createFreebets/createFreebetsSlice';

// Form
import { useFormState, useWatch } from 'react-hook-form';

// System
import { useSnackbar } from 'notistack';

// Types
import { AppDispatch } from 'src/app/store';
import { StoreInterface } from 'src/app/types';
import { useCreateFreebetsForm } from '@Forms/freebets/CreateFreebetsFormContext';
import axios from 'axios';
import Cookies from 'js-cookie';
import { useRevokeFreebetsForm } from '@Forms/freebets/RevokeFreebetsFormContext';
import { useApolloClient } from '@apollo/client';
import { PaginatedFreeBetsDocument } from '@Src/generated.graphql';

export const useCreateFreebets = () => {
  // Redux
  const dispatch = useDispatch<AppDispatch>();
  const apolloClient = useApolloClient();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const { showCancelFreebetsForm, showCreateFreebetsForm, showRevokeFreebetsForm } = useSelector(
    (state: StoreInterface) => state?.createFreebets
  );
  const rgs = useSelector((state: StoreInterface) => state.general.rgs);
  const {
    limit: freebetsLimit,
    page: freebetsPage,
    freebetTableFilters,
  } = useSelector((state: StoreInterface) => state.freebetsTable);

  // Other Hooks
  const { enqueueSnackbar } = useSnackbar();

  // Handle Form Cancels
  const handleCreateCancel = () => {
    if (isDirty) {
      return dispatch(setShowCancelFreebetsForm(true));
    } else {
      return dispatch(setShowCreateFreebetsForm(false));
    }
  };

  const handleCancelTakeMeBack = (action: string) => {
    if (action === 'back') {
      dispatch(setShowCancelFreebetsForm(false));
      dispatch(setShowCreateFreebetsForm(true));
    } else {
      reset();
      dispatch(setShowCancelFreebetsForm(false));
      dispatch(setShowCreateFreebetsForm(false));
    }
  };

  // Form Controls
  const { control, handleSubmit, reset } = useCreateFreebetsForm();
  const { isDirty, errors } = useFormState({ control });

  const {
    control: revokeControl,
    handleSubmit: revokeHandleSubmit,
    reset: revokeReset,
  } = useRevokeFreebetsForm();
  const { isDirty: revokeIsDirty, errors: revokeErrors } = useFormState({ control: revokeControl });
  const revokeType = useWatch({ control: revokeControl, name: 'revokeType' });
  const revokeFile = useWatch({ control: revokeControl, name: 'file' });
  const revokeRequestId = useWatch({ control: revokeControl, name: 'requestId' });

  const isRevokeTypeValid =
    revokeType === 'playerIds'
      ? revokeFile instanceof File
      : revokeType === 'requestId'
      ? Boolean(revokeRequestId?.trim())
      : true;

  const handleSubmitCreateFreebets = useCallback(
    (e?: BaseSyntheticEvent) => {
      handleSubmit(async (formData) => {
        setIsSubmitting(true);

        try {
          const token = Cookies.get('accessToken');
          const { campaignId, ...payload } = formData;
          await axios.post(
            `${process.env.REACT_APP_UPLOADS_API_PROXY_URL}/api/freebets/${campaignId}`,
            payload,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
                authorization: token ? `Bearer ${token}` : '',
                'x-rgs-id': rgs,
              },
            }
          );

          enqueueSnackbar('Freebets were successfully created', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          await apolloClient.query({
            query: PaginatedFreeBetsDocument,
            variables: {
              limit: freebetsLimit,
              page: freebetsPage,
              filter: freebetTableFilters,
              search: '',
            },
            fetchPolicy: 'network-only',
          });

          reset();
          dispatch(setShowCreateFreebetsForm(false));
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          enqueueSnackbar(message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      })(e);
    },
    [
      apolloClient,
      dispatch,
      enqueueSnackbar,
      freebetsLimit,
      freebetsPage,
      freebetTableFilters,
      handleSubmit,
      reset,
      rgs,
    ]
  );

  const handleSubmitRevokeFreebets = useCallback(
    (e?: BaseSyntheticEvent) => {
      revokeHandleSubmit(async (formData) => {
        setIsSubmitting(true);
        try {
          const token = Cookies.get('accessToken');
          const { campaignId, revokeType, file, requestId } = formData;
          const isPlayerIdsRevoke = revokeType === 'playerIds';
          const payload = isPlayerIdsRevoke
            ? (() => {
                const multipartData = new FormData();

                if (file instanceof File) {
                  multipartData.append('file', file, file.name);
                }

                return multipartData;
              })()
            : revokeType === 'requestId'
            ? { requestId }
            : { isFullRevoke: true };

          await axios.post(
            `${process.env.REACT_APP_UPLOADS_API_PROXY_URL}/api/freebets/${campaignId}/revoke`,
            payload,
            {
              headers: {
                'Content-Type': isPlayerIdsRevoke ? 'multipart/form-data' : 'application/json',
                authorization: token ? `Bearer ${token}` : '',
                'x-rgs-id': rgs,
              },
            }
          );

          enqueueSnackbar('Freebets were successfully revoked', {
            variant: 'success',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });

          await apolloClient.query({
            query: PaginatedFreeBetsDocument,
            variables: {
              limit: freebetsLimit,
              page: freebetsPage,
              filter: freebetTableFilters,
              search: '',
            },
            fetchPolicy: 'network-only',
          });

          revokeReset();
          dispatch(setShowRevokeFreebetsForm(false));
        } catch (error: unknown) {
          const message = error instanceof Error ? error.message : 'Unknown error';
          enqueueSnackbar(message, {
            variant: 'error',
            autoHideDuration: 2000,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      })(e);
    },
    [
      apolloClient,
      dispatch,
      enqueueSnackbar,
      freebetsLimit,
      freebetsPage,
      freebetTableFilters,
      revokeHandleSubmit,
      revokeReset,
      rgs,
    ]
  );

  const handleOpenCreateFreebetsModal = () => dispatch(setShowCreateFreebetsForm(true));

  const handleRevokeFreebetsModal = (arg: boolean) => dispatch(setShowRevokeFreebetsForm(arg));

  return {
    // Store
    showRevokeFreebetsForm,
    showCancelFreebetsForm,
    showCreateFreebetsForm,
    submittable: isDirty && Object.values(errors).length == 0 && !isSubmitting,
    isRevokeSubmittable:
      revokeIsDirty &&
      Object.values(revokeErrors).length == 0 &&
      isRevokeTypeValid &&
      !isSubmitting,
    // Actions
    handleCreateCancel,
    handleCancelTakeMeBack,
    setShowCreateFreebetsForm,
    setShowRevokeFreebetsForm,
    handleRevokeFreebetsModal,
    handleSubmitCreateFreebets,
    handleSubmitRevokeFreebets,
    handleOpenCreateFreebetsModal,
  };
};
